/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useFormikContext, useField } from 'formik';
import { useBestFitQuery } from '../../../Hooks/Api/useBestFitQuery';

/**
 * Custom useContractForm hook
 * @param contractIdentifier
 * @param allowedContracts
 */
export function useContractForm(contractIdentifier, allowedContracts) {
  const isComponentMounted = useRef(false);
  const selectedContractId = useRef(null);
  const { values, setFieldValue } = useFormikContext();
  const [contractData, setContractData] = useState(0);
  const [tab, setTab] = useState(0);
  const [meta] = useField(contractIdentifier);

  const {
    data, isLoading,
  } = useBestFitQuery('rateBundle', {
    studioId: values.studioId,
  });

  /**
     *
     * @param rawContractData
     * @returns {*}
     */
  const getFilteredContractData = useCallback((rawContractData) => {
    let filteredContractData = rawContractData;
    if (allowedContracts.length > 0) {
      filteredContractData = rawContractData.filter((contract) => allowedContracts.includes(contract.id.toString()));
    }
    return filteredContractData;
  }, [allowedContracts]);

  /**
     * Add field values to formik context
     */
  const addContractToFormikValues = useCallback(() => {
    const selectedContract = contractData !== 0 ? contractData[tab] : getFilteredContractData(data)[0];
    let selectedTerm = selectedContract.terms.find((contract) => contract.id.toString() === meta.value);
    if (!selectedTerm) {
      [selectedTerm] = selectedContract.terms;
    }
    setFieldValue('contractName', selectedContract.name);
    setFieldValue('contractDescription', selectedContract.description);
    setFieldValue('contractSubDescription', selectedContract.subDescription);
    setFieldValue('contractFootnote', selectedContract.footnote);
    setFieldValue('contractTerm', selectedTerm);
    setFieldValue('contractStartDateOfUse', selectedTerm.contractStartDateOfUse);
    setFieldValue('defaultContractStartDate', selectedTerm.defaultContractStartDate);
  }, [contractData, data, getFilteredContractData, meta.value, setFieldValue, tab]);

  /**
     * Preselect first contract
     */
  useEffect(() => {
    if (!data) return;

    const filteredContractData = getFilteredContractData(data);

    if (!isComponentMounted.current) {
      isComponentMounted.current = true;
      setContractData(filteredContractData);
    }

    if (filteredContractData.length > 0) {
      const selectedTerm = filteredContractData[tab].terms.find((term) => term.id.toString() === meta.value.toString());

      if (!selectedTerm) {
        setFieldValue(contractIdentifier, filteredContractData[tab].terms[0].id);
      }
    }
  }, [contractIdentifier, data, getFilteredContractData, meta.value, setFieldValue, tab]);

  /**
     * Set the selected contract and form values
     */
  useEffect(() => {
    if (data && meta.value && meta.value !== selectedContractId.current) {
      selectedContractId.current = meta.value;
      addContractToFormikValues();
    }
  }, [addContractToFormikValues, data, meta.value]);

  return {
    isLoading, contractData, tab, setTab,
  };
}
